import { useRouter } from 'expo-router'
import moment from 'moment'
import React from 'react'
import { Dimensions, Text, View } from 'react-native'
import { ScaledSheet } from 'react-native-size-matters'
import { useDispatch, useSelector } from 'react-redux'
import { FormButton } from '~components/FormButton'
import { FormInput } from '~components/FormInput'
import { FormInputButton } from '~components/FormInputButton'
import ModalScreen, { getModalStyleDefinition, ModalFooter } from '~components/ModalScreen'
import { EVENT_CHAT_CREATED, EVENT_COMMUNITY_TEMPLATE_USED } from '~constants/Analytics'
import { analytics, logEvent } from '~providers/firebase'
import { ApplicationState } from '~redux'
import { setChat } from '~redux/chat/actions'
import ChatService from '~services/chat'
import { useTheme } from '~theme/ThemeManager'
import { CreateChatType } from '~types'
import { actions, useNewChatContext } from './state'

export default function BlankLayout() {
  const styles = useStyles()
  const [state, dispatch] = useNewChatContext()
  const router = useRouter()
  const { user, profile } = useSelector((state: ApplicationState) => state.auth)
  const globalDispatch = useDispatch()

  const dismiss = React.useCallback(() => {
    dispatch(actions.reset())
  }, [dispatch])

  const folder = () => {
    router.navigate('./folder')
  }

  const create = async () => {
    if (!user) {
      console.error('User not found')
      return
    }

    if (!state.class.id) {
      console.error('Class not found')
      return
    }

    if (!(state.layout || state.template)) {
      console.error('Layout or template not found')
      return
    }

    if (state.loading) {
      return
    }

    dispatch(actions.setLoading(true))

    const props: CreateChatType = {
      name: state.name.length > 0 ? state.name : moment().format('MMMM Do YYYY, h:mm:ss a'),
      level: '',
      layout: state.template?.layout ?? state.layout ?? { rows: 1, columns: 1 },
      sections: state.template?.sections,
      author_id: user.uid,
      author_name: profile.displayName,
      template_id: state.template?.id,
    }

    const chat = await ChatService.createChat(`classes/${state.class.id}`, props)

    try {
      await logEvent(analytics, EVENT_CHAT_CREATED)
      if (state.template?.id !== undefined) {
        await logEvent(analytics, EVENT_COMMUNITY_TEMPLATE_USED, {
          templateId: state.template.id,
          templateName: state.template.name,
          type: state.template.type,
        })
      }
    } catch (error) {
      console.error('Error logging chat created event:', error)
    }

    globalDispatch(setChat(chat))
    dispatch(actions.reset())

    router.dismissTo(`/folders/${state.class.id}`, { withAnchor: true })
    router.navigate(`/folders/${state.class.id}/chats/${chat.id}`)
  }

  return (
    <ModalScreen
      onDismiss={dismiss}
      dismissTo={state.dismissTo}
      Footer={
        <ModalFooter>
          <FormButton
            onPress={router.back}
            title={'Back'}
            style={styles.backButton}
            textStyle={styles.backButtonText}
            disabled={state.loading}
          />
          <FormButton
            onPress={create}
            title={'Create Chat'}
            style={styles.button}
            disabled={
              (state.template === undefined && state.layout === undefined) ||
              state.class.id.length === 0 ||
              state.loading
            }
          />
        </ModalFooter>
      }
    >
      <View style={styles.inner}>
        <View style={styles.content}>
          {/* {progress(1)} */}

          <View style={styles.titleContainer}>
            <Text style={styles.title}>
              {state.template ? 'New chat from template' : 'New Chat'}
            </Text>
            <Text style={styles.subtitle}>
              {state.template ? 'Personalise this template' : 'A few details to start'}
            </Text>
          </View>

          <View>
            <FormInput
              label="Name of chat"
              value={state.name}
              onChangeText={(text) => dispatch(actions.setName(text))}
              maxLength={128}
            />

            {state.showFolderSelection && (
              <FormInputButton
                input={{
                  label: 'Which folder would you like to add this chat to?',
                  value: state.class.name,
                  editable: false,
                  placeholder: 'Select a folder',
                  style: styles.folderInput,
                }}
                button={{
                  title: 'Select a folder',
                  onPress: folder,
                }}
              />
            )}
          </View>
        </View>
      </View>
    </ModalScreen>
  )
}

const useStyles = () => {
  const { theme, mode } = useTheme()
  return ScaledSheet.create({
    ...getModalStyleDefinition(theme, mode),
    listItem: {
      marginVertical: '5@mvs',
      padding: '5@ms',
      backgroundColor: theme.white,
    },
    listItemSelected: {
      backgroundColor: theme.pink[200],
    },
    layoutsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    backgroundContainerStyle: {
      borderColor: theme.primary,
    },
    textContainerStyle: {
      borderColor: theme.primary,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignContent: 'center',
      alignItems: 'center',
      paddingTop: '10@mvs0.8',
      paddingStart: '15@ms',
      paddingHorizontal: '10@mvs0.8',
    },
    folderInput: {
      color: theme.primary,
    },
    sourceContainer: {
      backgroundColor: theme.white,
      borderWidth: 1,
      borderColor: theme[300],
      borderRadius: '5@ms',
      marginHorizontal: '10@ms',
      width: Dimensions.get('window').width > 640 ? '90@ms' : undefined,
      flexDirection: Dimensions.get('window').width > 640 ? 'column' : 'row',
      padding: Dimensions.get('window').width > 640 ? '15@ms' : '10@ms',
      alignItems: 'center',
    },
    sourceIcon: {
      marginTop: Dimensions.get('window').width > 640 ? '10@ms' : undefined,
      marginBottom: Dimensions.get('window').width > 640 ? '20@ms' : undefined,
      marginRight: Dimensions.get('window').width > 640 ? undefined : '10@ms',
    },
    sourceText: {
      fontSize: '10@ms',
      textAlign: 'center',
      fontWeight: '700',
      color: theme[800],
    },
    column: {
      justifyContent: 'center',
    },
    list: {
      alignSelf: 'center',
    },
    listVerticalSeparator: {
      height: Dimensions.get('window').width > 640 ? '20@mvs' : '10@mvs',
    },
  })
}
